// Copyright (c) Laserfiche.
// Licensed under the MIT License. See LICENSE in the project root for license information.

import {
  Component,
  ChangeDetectorRef,
  Input,
  ViewChild,
  OnDestroy,
  ComponentRef,
  AfterViewInit,
  EventEmitter,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LfFieldAdhocContainerService as LfFieldAdhocContainerService } from './lf-field-adhoc-container.service';
import { AdhocFieldConnectorService } from './lf-field-adhoc-connector.service';
import { AdhocFieldInfo } from './lf-field-adhoc-container-types';
import { Subscription } from 'rxjs';
import { LfFieldAddRemoveComponent } from './lf-field-add-remove/lf-field-add-remove.component';
import { AppLocalizationService } from '@laserfiche/lf-ui-components/internal-shared';
import { FieldValue, FieldValues, LfFieldInfo } from '../field-components/utils/lf-field-types';
import { LfFieldComponent } from '../field-components/lf-field/lf-field.component';
import { LfFieldMultivalueComponent } from '../field-components/lf-field-multivalue/lf-field-multivalue.component';
import { LfFieldMetadataConnectorService } from '../lf-field-metadata-connector.service';
import { LfFieldContainerDirective } from '../lf-field-container.directive';
import { LfFieldViewDirective } from '../lf-field-view.directive';
import { CoreUtils } from '@laserfiche/lf-js-utils';

@Component({
  selector: 'lfint-field-adhoc-container, lf-field-adhoc-container',
  templateUrl: './lf-field-adhoc-container.component.html',
  styleUrls: ['./lf-field-adhoc-container.component.css'],
  providers: [AdhocFieldConnectorService],
  standalone: true,
  imports: [CommonModule, LfFieldViewDirective, LfFieldAddRemoveComponent],
})
export class LfFieldAdhocContainerComponent extends LfFieldContainerDirective implements OnDestroy, AfterViewInit {
  /**@internal */
  private adhocFieldConnectorService = inject(AdhocFieldConnectorService);
  /**@internal */
  private ref = inject(ChangeDetectorRef);
  /**@internal */
  private localizationService = inject(AppLocalizationService);

  /** @internal */
  @ViewChild(LfFieldAddRemoveComponent) addRemoveComponent!: LfFieldAddRemoveComponent;
  @Output() dialogOpened = new EventEmitter<void>();
  @Output() dialogClosed = new EventEmitter<void>();

  /** @internal */
  readonly ADD_REMOVE_FIELDS = this.localizationService.getStringLaserficheObservable('ADD_REMOVE_FIELDS');

  /** @internal */
  readonly NO_ADDITIONAL_FIELDS_ASSIGNED = this.localizationService.getStringLaserficheObservable(
    'NO_ADDITIONAL_FIELDS_ASSIGNED'
  );

  /** @internal */
  showAdhocModal: boolean = false;
  /** @internal */
  adhocFieldContainerService!: LfFieldAdhocContainerService;
  /** @internal */
  templateFields: number[] = [];
  /** @internal */
  private selectedFieldIds: Set<number> = new Set<number>();
  /** @internal */
  private templateFieldsSub: Subscription | undefined;

  /** @internal */
  constructor() {
    super();
  }

  /** @internal */
  ngAfterViewInit() {
    this.templateFieldsSub = this.metadataFieldConnectorService
      .getTemplateFields()
      .subscribe(async (currentTemplateFields) => {
        this.templateFields = currentTemplateFields;
        await this.refreshFieldsAsync();
      });
  }

  /** @internal */
  ngOnDestroy() {
    this.templateFieldsSub?.unsubscribe();
    this.resetComponentValues();
  }

  @Input()
  getFieldValues = (): { [fieldName: string]: FieldValue } => {
    const fieldValues: { [fieldName: string]: FieldValue } = {};
    this.componentRefs?.forEach((componentRef) => {
      const fieldValue = componentRef.instance.getFieldValue();
      const fieldName = fieldValue.fieldName ?? undefined;
      if (fieldName !== undefined) {
        fieldValues[fieldName] = fieldValue;
      } else {
        console.warn('FieldValue.fieldName is undefined, skipping.');
      }
    });
    return fieldValues;
  };

  @Input()
  clearAsync = async (): Promise<void> => {
    this.resetComponentValues();
    this.metadataFieldConnectorService.clearAllFieldValues();
    await this.resetFieldDataAsync([]);
    this.ref.markForCheck();
  };

  @Input()
  initAsync = async (adhocFieldContainerService: LfFieldAdhocContainerService): Promise<void> => {
    this.resetComponentValues();
    this.adhocFieldContainerService = CoreUtils.validateDefined(
      adhocFieldContainerService,
      'adhocFieldContainerService'
    );
    this.ref.markForCheck();
  };

  @Input()
  resetFieldDataAsync = async (fields: { value: FieldValue; definition: LfFieldInfo }[]): Promise<void> => {
    this.loadSelectedFieldValues(fields);
    await this.refreshFieldsAsync();
    this.metadataFieldConnectorService.updatedAdhocFieldData();
    this.ref.markForCheck();
  };

  @Input()
  updateFieldValuesAsync = async (values: FieldValue[]): Promise<void> => {
    values.forEach((field) => {
      this.allFieldValues[field.fieldId] = field;
    });

    this.metadataFieldConnectorService.setAllFieldValues(this.allFieldValues);
    await this.refreshFieldsAsync();
    this.metadataFieldConnectorService.updatedAdhocFieldData();
    this.ref.markForCheck();
  };

  /** @internal */
  private resetComponentValues() {
    this.templateFields = [];
    this.metadataFieldConnectorService.setAllFieldValues({});
    this.adhocFieldConnectorService.setSelectedFieldIds(new Set<number>());
  }

  /** @internal */
  async renderFieldsAsync(fieldInfos: (LfFieldInfo | AdhocFieldInfo)[]): Promise<void> {
    const vf = this.lfFieldView.viewContainerRef;
    vf.clear();
    this.componentRefs = [];
    this.groupComponentRefs = [];
    // The template reads componentRefs through fieldsEmpty(). The field views created below schedule
    // a change detection that refreshes only them, so without this the empty-state message stays
    // stale (NG0100 in dev mode). The first field is pushed before that change detection runs.
    this.ref.markForCheck();

    for (const fieldInfo of fieldInfos) {
      const values = this.getValuesById(fieldInfo.id) ?? [];
      if (fieldInfo.isMultiValue) {
        const multivalueComponentRef = vf.createComponent(LfFieldMultivalueComponent);
        this.componentRefs.push(multivalueComponentRef);
        await this.initializeMultivalueComponentAsync(multivalueComponentRef, fieldInfo, values);
      } else {
        const fieldComponentRef = vf.createComponent(LfFieldComponent);
        this.componentRefs.push(fieldComponentRef);
        await this.initializeFieldComponentAsync(fieldComponentRef, fieldInfo, values?.length > 0 ? values[0] : '');
      }
    }
  }

  /** @internal */
  async fieldComponentInitAsync(
    fieldComponentRef: ComponentRef<LfFieldComponent>,
    fieldInfo: LfFieldInfo,
    value: string
  ) {
    await fieldComponentRef.instance.initAsync(fieldInfo, value);
  }

  /** @internal */
  async multivalueComponentInitAsync(
    multivalueFieldComponentRef: ComponentRef<LfFieldMultivalueComponent>,
    fieldInfo: LfFieldInfo,
    values: string[]
  ) {
    await multivalueFieldComponentRef.instance.initAsync(fieldInfo, values);
  }

  /** @internal */
  private loadSelectedFieldValues(fields: { value: FieldValue; definition: LfFieldInfo }[]): void {
    this.getInitialSelectedOptions(fields);
    this.adhocFieldConnectorService.setSelectedFieldIds(this.selectedFieldIds);
  }

  /** @internal */
  getMappedFieldValues(fieldValues: FieldValue[]): FieldValues {
    const mappedFields: FieldValues = {};
    fieldValues.forEach((fieldValue) => {
      mappedFields[fieldValue.fieldId] = fieldValue;
    });
    return mappedFields;
  }

  /** @internal */
  getFieldIds(fieldValues: FieldValue[]): Set<number> {
    const fieldsSet = new Set<number>();
    fieldValues.forEach((fieldValue) => {
      fieldsSet.add(fieldValue.fieldId);
    });
    return fieldsSet;
  }

  /** @internal */
  private getInitialSelectedOptions(fields: { value: FieldValue; definition: LfFieldInfo }[]): void {
    const mappedFields = fields ?? [];

    const fieldInfos: LfFieldInfo[] = [];
    const fieldValues: FieldValue[] = [];
    mappedFields.forEach((field) => {
      if (field.value && field.definition) {
        fieldInfos.push(field.definition);
        fieldValues.push(field.value);
      } else {
        // skip field
      }
    });

    this.allFieldInfos = fieldInfos;
    this.allFieldValues = this.getMappedFieldValues(fieldValues);
    this.adhocFieldConnectorService.setAllFieldInfos(this.allFieldInfos);
    this.selectedFieldIds = this.getFieldIds(fieldValues);
    this.metadataFieldConnectorService.setAllFieldValues(this.allFieldValues);
  }

  /** @internal */
  fieldsEmpty() {
    const isEmpty = !this.componentRefs || this.componentRefs?.length === 0;
    return isEmpty;
  }

  /** @internal */
  async addRemoveFieldsAsync(adhocPanel?: HTMLElement): Promise<void> {
    this.toggleAdhocModal(true, adhocPanel);
    await this.addRemoveComponent.initAsync(this.adhocFieldContainerService);
    const fieldDefinitions = this.adhocFieldConnectorService.getAllFieldInfos();
    this.updateTemplateFields(fieldDefinitions);
    await this.addRemoveComponent.updateSortedFieldInfos();
  }

  /** @internal */
  async onClickBackAsync(): Promise<void> {
    this.toggleAdhocModal(false);
    await this.refreshFieldsAsync();
  }

  /** @internal */
  private toggleAdhocModal(open: boolean, panel?: HTMLElement) {
    this.showAdhocModal = open;
    this.metadataFieldConnectorService.setAddRemoveContainerToggled(open);
    if (open) {
      this.dialogOpened.emit();
      setTimeout(() => panel?.focus());
    } else {
      this.dialogClosed.emit();
    }
  }

  /** @internal */
  async refreshFieldsAsync(): Promise<void> {
    const fieldValues = this.metadataFieldConnectorService.getAllFieldValues() ?? {};
    this.allFieldValues = { ...this.allFieldValues, ...fieldValues };
    this.selectedFieldIds = this.adhocFieldConnectorService.getSelectedFieldIds();
    const fieldInfosToRender = this.getSelectedFieldInfos();
    await this.renderFieldsAsync(fieldInfosToRender);
  }

  /** @internal */
  getSelectedFieldInfos(): LfFieldInfo[] {
    this.allFieldInfos = this.adhocFieldConnectorService.getAllFieldInfos();
    this.updateTemplateFields(this.allFieldInfos);
    const fieldInfos: LfFieldInfo[] = this.allFieldInfos?.filter(
      (fieldInfo) => this.selectedFieldIds.has(fieldInfo.id) && !(fieldInfo as AdhocFieldInfo).inTemplateSelected
    );
    return fieldInfos ?? [];
  }

  /** @internal */
  private updateTemplateFields(fieldInfos: AdhocFieldInfo[]) {
    fieldInfos?.forEach((fieldInfo) => {
      if (this.templateFields?.includes(fieldInfo.id)) {
        fieldInfo.inTemplateSelected = true;
      } else {
        fieldInfo.inTemplateSelected = false;
      }
    });
  }

  /** @internal */
  async onFieldValueChangedAsync(values: string[], fieldInfo: LfFieldInfo): Promise<void> {
    this.fieldValuesChanged.emit(this.isValid());
    this.setFieldValue(fieldInfo.id, values);
  }
}
